import { useState } from "react";
import type { Client } from "openapi-fetch";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiErrorMessage } from "@/lib/api-error";
import { useDemoApi } from "./DemoProvider";

// Hand-written: POST /demo/bank-transactions is demo scaffolding, hidden from every OpenAPI document (spec D8).
export type DemoBankAccount = "Current" | "Savings";

export interface AddDemoBankTransactionRequest {
  account: DemoBankAccount;
  /** Positive for money in, negative for money out. */
  amount: number;
  description: string;
  payee?: string | null;
  /** yyyy-MM-dd; today when left out. */
  date?: string | null;
}

export interface DemoBankTransaction {
  id: string;
  account: DemoBankAccount;
  date: string;
  amount: number;
  description: string;
  payee: string | null;
}

interface DemoBankPaths {
  "/v1/demo/bank-transactions": {
    parameters: { query?: never; header?: never; path?: never; cookie?: never };
    post: {
      parameters: { query?: never; header?: never; path?: never; cookie?: never };
      requestBody: { content: { "application/json": AddDemoBankTransactionRequest } };
      responses: {
        201: { headers: Record<string, unknown>; content: { "application/json": DemoBankTransaction } };
        default: { headers: Record<string, unknown>; content: { "application/problem+json": unknown } };
      };
    };
  };
}

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** The Demo tag on every made-up screen (D11), so nobody takes it for a real bank. */
export function DemoTag() {
  return (
    <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
      Demo
    </span>
  );
}

/**
 * "Add a bank transaction" in a demo's bank lines: the line lands in the fake bank, which re-syncs at once, so it shows
 * here within seconds and the rules, learned coding or the model file it like any other.
 */
export function AddBankTransaction({
  open,
  onOpenChange,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: () => void;
}) {
  const api = useDemoApi() as unknown as Client<DemoBankPaths>;
  const [account, setAccount] = useState<DemoBankAccount>("Current");
  const [direction, setDirection] = useState<"out" | "in">("out");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState<string | null>(today());
  const [payee, setPayee] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const value = Number(amount);
  const valid = amount.trim() !== "" && Number.isFinite(value) && value > 0 && description.trim() !== "";

  async function add() {
    if (!valid || saving) return;
    setSaving(true);
    const { error } = await api.POST("/v1/demo/bank-transactions", {
      body: {
        account,
        amount: direction === "out" ? -value : value,
        description: description.trim(),
        payee: payee.trim() || null,
        date,
      },
    });
    setSaving(false);
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't add the bank transaction."));
      return;
    }
    toast.success("Added to the demo bank. It shows here in a few seconds, categorised if a rule or the model knows it.");
    setAmount("");
    setPayee("");
    setDescription("");
    onOpenChange(false);
    onAdded();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Add a bank transaction <DemoTag />
          </DialogTitle>
          <DialogDescription>
            A made-up transaction in the demo bank. Watch Dolfin match or categorise it, then post it to the books.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="demo-line-account">Account</Label>
            <Select value={account} onValueChange={(v) => setAccount(v as DemoBankAccount)}>
              <SelectTrigger id="demo-line-account">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Current">Business Current Account</SelectItem>
                <SelectItem value="Savings">Business Savings</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Direction</Label>
              <Tabs value={direction} onValueChange={(v) => setDirection(v as "out" | "in")}>
                <TabsList className="w-full">
                  <TabsTrigger value="out" className="flex-1">
                    Money out
                  </TabsTrigger>
                  <TabsTrigger value="in" className="flex-1">
                    Money in
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="demo-line-amount">Amount (GBP)</Label>
              <Input
                id="demo-line-amount"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="tabular-nums"
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Date</Label>
            <DatePicker value={date} onChange={setDate} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="demo-line-payee">Payee</Label>
            <Input id="demo-line-payee" maxLength={100} placeholder="Who paid or was paid" value={payee} onChange={(e) => setPayee(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="demo-line-description">Description</Label>
            <Input
              id="demo-line-description"
              maxLength={140}
              placeholder="As it reads on the statement, e.g. CARD PAYMENT TESCO"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || saving}>
              {saving ? <Spinner className="size-4" /> : <Plus className="size-4" />}
              Add to the demo bank
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
